import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CalendarDays, LocateFixed, Loader2, MapPin, Navigation, Search, X } from "lucide-react";
import { toast } from "sonner";
import { formatEventDate, useApi } from "@/lib/api";
import { distanceKm, eventsAtPlace, formatKm } from "@/lib/geo";
import { Container, PageHeader } from "@/components/site/PageHeader";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LIEUX } from "@/components/site/nav";
import { CATEGORY_STYLE, GoButton, PlacesMap } from "@/components/site/PlacesMap";
import { PlaceHistory } from "@/components/site/PlaceHistory";
import { OpenBadge, PlaceHours } from "@/components/site/PlaceHours";
import { hasHours, isOpenNow } from "@/lib/hours";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const PlaceSearch = ({ value, onChange, count }) => (
  <div className="flex flex-wrap items-center gap-3 mb-4">
    <label className="flex items-center flex-1 min-w-[240px] max-w-xl border border-white/30 focus-within:border-primary transition-colors">
      <Search className="w-4 h-4 text-zinc-400 ml-4 shrink-0" />
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Chercher un lieu ou une ville…"
        aria-label="Chercher un lieu ou une ville" data-testid="places-search-input"
        className="flex-1 min-w-0 bg-transparent text-white placeholder:text-zinc-400 px-3 h-12 outline-none" />
      {value && (
        <button type="button" onClick={() => onChange("")} aria-label="Effacer" data-testid="places-search-clear" className="px-4 h-12 text-zinc-300 hover:text-primary"><X className="w-4 h-4" /></button>
      )}
    </label>
    {value && <p className="text-sm text-zinc-300" data-testid="places-search-count">{count} lieu{count > 1 ? "x" : ""} trouvé{count > 1 ? "s" : ""}</p>}
  </div>
);

const ALL = { key: "tous", label: "Tous les lieux" };
const INTRO = {
  tous: "Disquaires, salles de concert, bars, restaurants, clubs, cafés, galeries et librairies : les adresses LGTDZ, en France comme à l'étranger.",
  disquaires: "Les disquaires où l'on digge : bacs hip-hop, soul, funk, jazz et pépites rares, en France comme à l'étranger.",
  salles: "Les salles où LGTDZ organise et soutient ses concerts.",
  bars: "Les bars et cafés qui accueillent les cartes blanches et émissions live LGTDZ.",
  clubs: "Là où on pense et on danse dans l'intimité jusqu'à point d'heure\u00a0!",
  cafes: "Les cafés où l'on se pose, écoute et refait le monde autour d'un son.",
  galeries: "Les galeries qui accueillent expos, vernissages et rencontres autour de la culture hip-hop.",
  librairies: "Les librairies où l'on trouve livres, BD et revues sur le hip-hop, la musique et la culture urbaine.",
};

const tabCls = "rounded-none font-display uppercase text-lg px-6 py-3 text-zinc-400 data-[state=active]:bg-white data-[state=active]:text-black";

const Legend = ({ showEvents, onToggleEvents, eventCount }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
    <ul className="flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-300" data-testid="places-map-legend">
      {Object.entries(CATEGORY_STYLE).map(([k, s]) => (
        <li key={k} className="flex items-center gap-2"><span className="w-3 h-3 inline-block" style={{ background: s.color }} /> {s.label}</li>
      ))}
      {showEvents && <li className="flex items-center gap-2"><span className="w-3.5 h-3.5 inline-block rounded-full border-[3px] border-primary" /> Prochain concert</li>}
    </ul>
    <button type="button" onClick={onToggleEvents} aria-pressed={showEvents} data-testid="places-map-events-toggle"
      className={`h-10 px-4 border text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${showEvents ? "bg-primary border-primary text-white" : "border-white/30 text-white hover:border-primary hover:text-primary"}`}>
      {showEvents ? "Masquer les concerts" : "Afficher les prochains concerts"}
    </button>
    {showEvents && eventCount === 0 && <p className="w-full text-sm text-zinc-400" data-testid="places-map-no-events">Aucun concert à venir pour l'instant : ils apparaîtront ici dès leur ajout à l'agenda.</p>}
  </div>
);

const PlaceRow = ({ p, i, active, onLocate }) => (
  <li className={`group grid md:grid-cols-[80px_1.2fr_1fr] gap-4 md:gap-10 py-10 border-b border-white/10 reveal ${active ? "bg-white/[0.03]" : ""}`} style={{ animationDelay: `${i * 50}ms` }} data-testid={`place-item-${p.id}`}>
    <span className="font-display text-3xl text-primary">{String(i + 1).padStart(2, "0")}</span>
    <div className="min-w-0">
      <h3 className="font-display uppercase text-4xl lg:text-6xl leading-[0.9] text-white group-hover:text-primary transition-colors duration-200 break-words">{p.name}</h3>
      <ul className="flex flex-wrap gap-2 mt-4" data-testid={`place-categories-${p.id}`}>
        {(p.categories?.length ? p.categories : [p.category]).map((c) => (
          <li key={c} className="inline-flex items-center gap-2 border border-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-200">
            <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY_STYLE[c]?.color }} />{CATEGORY_STYLE[c]?.label}
          </li>
        ))}
        <li><OpenBadge hours={p.hours} testId={`place-open-${p.id}`} /></li>
      </ul>
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mt-3">
        <MapPin className="w-4 h-4" /> {p.city} · {p.country}
        {typeof p.distance === "number" && <span className="text-primary" data-testid={`place-distance-${p.id}`}>· à {formatKm(p.distance)}</span>}
      </p>
      <PlaceHours placeId={p.id} hours={p.hours} source={p.hours_source} />
      {p.upcoming?.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid={`place-concerts-${p.id}`}>
          <li className="text-[11px] uppercase tracking-[0.25em] font-bold text-primary">Prochains concerts ici</li>
          {p.upcoming.map((e) => (
            <li key={e.id}>
              <Link to={`/agenda/${e.slug}`} className="flex items-baseline gap-3 text-white hover:text-primary transition-colors" data-testid={`place-concert-${e.id}`}>
                <CalendarDays className="w-4 h-4 shrink-0 self-center text-zinc-400" />
                <span className="text-sm text-zinc-400 shrink-0">{formatEventDate(e.date)}</span>
                <span className="font-semibold truncate">{e.title}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <PlaceHistory placeId={p.id} events={p.past} />
      {typeof p.lat === "number" && (
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
          <button type="button" onClick={() => onLocate(p.id)} data-testid={`place-locate-${p.id}`}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-white underline decoration-primary decoration-2 underline-offset-4 hover:text-primary transition-colors">
            <Navigation className="w-3.5 h-3.5" /> Voir sur la carte
          </button>
          <GoButton place={p} testId={`place-go-${p.id}`} />
        </div>
      )}
    </div>
    <div className="self-center space-y-5">
      {p.image && (
        <img src={p.image} alt={p.name} loading="lazy" data-testid={`place-photo-${p.id}`}
          className="w-full aspect-[4/3] object-cover border border-white/10" />
      )}
      <p className="text-zinc-300 leading-relaxed">{p.description}</p>
    </div>
  </li>
);

const OpenNowButton = ({ active, onToggle }) => (
  <button type="button" onClick={onToggle} aria-pressed={active} data-testid="places-open-now-btn"
    className={`h-10 px-4 inline-flex items-center gap-2 border text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${active ? "bg-emerald-500 border-emerald-500 text-black" : "border-white/30 text-white hover:border-emerald-400 hover:text-emerald-400"}`}>
    <span className={`w-2 h-2 rounded-full ${active ? "bg-black animate-pulse" : "bg-emerald-400"}`} /> Ouvert maintenant
  </button>
);

const NearMeButton = ({ onLocated, active, onClear }) => {
  const [busy, setBusy] = useState(false);
  const locate = () => {
    if (!navigator.geolocation) {
      toast.error("La géolocalisation n'est pas disponible sur cet appareil.");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setBusy(false); onLocated({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      () => { setBusy(false); toast.error("Position introuvable : autorise la localisation dans ton navigateur."); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };
  return (
    <button type="button" onClick={active ? onClear : locate} disabled={busy} data-testid="places-near-me-btn" aria-pressed={active}
      className={`h-10 px-4 inline-flex items-center gap-2 border text-[11px] uppercase tracking-[0.2em] font-bold transition-colors disabled:opacity-60 ${active ? "bg-white border-white text-black" : "border-white/30 text-white hover:border-primary hover:text-primary"}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />} {active ? "Voir tous les lieux" : "Autour de moi"}
    </button>
  );
};

export default function Places() {
  const { category = ALL.key } = useParams();
  const navigate = useNavigate();
  const [focusId, setFocusId] = useState(null);
  const [showEvents, setShowEvents] = useState(true);
  const [userPos, setUserPos] = useState(null);
  const [q, setQ] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!openOnly) return undefined;
    setNow(new Date());
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, [openOnly]);
  const { data: history } = useApi("/places/history");
  const { data } = useApi("/places", category === ALL.key ? {} : { category });
  const { data: evData } = useApi("/events", { when: "upcoming", limit: 200 });
  const allEvents = useMemo(() => (Array.isArray(evData) ? evData : []), [evData]);
  const events = useMemo(() => (showEvents ? allEvents : []), [showEvents, allEvents]);
  const places = useMemo(() => {
    const needle = norm(q.trim());
    const list = (Array.isArray(data) ? data : [])
      .filter((p) => !needle || norm(`${p.name} ${p.city} ${p.country}`).includes(needle))
      .filter((p) => !openOnly || isOpenNow(p.hours, now) === true)
      .map((p) => ({
      ...p,
      upcoming: eventsAtPlace(p, allEvents),
      past: Array.isArray(history?.[p.id]) ? history[p.id] : [],
      distance: userPos && typeof p.lat === "number" ? distanceKm(userPos, p) : undefined,
    }));
    return userPos ? [...list].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity)) : list;
  }, [data, allEvents, userPos, q, history, openOnly, now]);
  const withHours = (Array.isArray(data) ? data : []).filter((p) => hasHours(p.hours)).length;
  const tabs = [ALL, ...LIEUX];
  const current = tabs.find((l) => l.key === category) || ALL;

  const locate = (id) => {
    setFocusId(id);
    document.getElementById("carte")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <>
      <PageHeader top="Lieux" bottom={current.key === ALL.key ? "La carte" : current.label} intro={INTRO[current.key]} testId="places-header" />
      <Container className="py-12 border-b border-white/10">
        <Tabs value={current.key} onValueChange={(v) => { setFocusId(null); navigate(v === ALL.key ? "/lieux" : `/lieux/${v}`); }}>
          <TabsList className="rounded-none bg-transparent border border-white/20 p-0 h-auto flex-wrap">
            {tabs.map((l) => (
              <TabsTrigger key={l.key} value={l.key} className={tabCls} data-testid={`places-tab-${l.key}`}>{l.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </Container>
      <Container className="pt-12" >
        <div id="carte" className="scroll-mt-24">
          <PlaceSearch value={q} onChange={(v) => { setFocusId(null); setQ(v); }} count={places.length} />
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-zinc-300 text-sm" data-testid="places-near-me-hint">
              {userPos ? "Les lieux sont classés du plus proche au plus loin." : "Trouve les adresses LGTDZ les plus proches de toi."}
              {openOnly && <span className="text-emerald-400"> Seuls les lieux ouverts en ce moment sont affichés.</span>}
            </p>
            <div className="flex flex-wrap gap-2">
              <OpenNowButton active={openOnly} onToggle={() => { setFocusId(null); setOpenOnly((v) => !v); }} />
              <NearMeButton active={!!userPos} onLocated={(pos) => { setFocusId(null); setUserPos(pos); }} onClear={() => setUserPos(null)} />
            </div>
          </div>
          <PlacesMap places={places} events={events} focusId={focusId} onSelect={setFocusId} userPos={userPos} category={category} />
          <Legend showEvents={showEvents} onToggleEvents={() => setShowEvents((v) => !v)} eventCount={events.length} />
        </div>
      </Container>
      <Container className="py-12">
        <ul data-testid="places-list">
          {places.length === 0 && q && <li className="py-10 text-zinc-300" data-testid="places-search-empty">Aucun lieu ne correspond à « {q} ».</li>}
          {places.length === 0 && !q && openOnly && (
            <li className="py-10 text-zinc-300" data-testid="places-open-now-empty">
              Aucun lieu ouvert en ce moment{current.key !== ALL.key ? " dans cette catégorie" : ""}.
              {withHours === 0 ? " Les horaires des lieux ne sont pas encore renseignés." : ` (${withHours} lieu${withHours > 1 ? "x ont leurs" : " a ses"} horaires renseignés.)`}
              {" "}<button type="button" onClick={() => setOpenOnly(false)} data-testid="places-open-now-reset" className="text-white underline decoration-primary underline-offset-4 hover:text-primary">Voir tous les lieux</button>
            </li>
          )}
          {places.map((p, i) => <PlaceRow key={p.id} p={p} i={i} active={p.id === focusId} onLocate={locate} />)}
        </ul>
      </Container>
    </>
  );
}
